import splitHoursMinutes from "@/helpers/splitHoursMinutes";
import { isOfficeHours } from "@/services/isOfficeHours";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { ReactSVG } from "react-svg";
import { siteOptions } from "../../data/siteOptions";
import { formatPhone } from "../../services/formatPhone";
import { socialLinks } from "./FooterData";
import FooterStyles from "./FooterStyles";
import holidays from "@/services/holidays";
import { useMediaQuery } from "@/helpers/hooks";

import { useRouter } from "next/router";

const FooterComponent = ({
  phone_number,
  isHome = false,
  currentSlug = "",
}) => {
  const [conditionalPhone, setConditionalPhone] = useState("");
  const [mouseOver, setMouseOver] = useState(null);
  const isMobile = useMediaQuery('(max-width: 992px)');

  const router = useRouter();

  dayjs.extend(advancedFormat);
  dayjs.extend(timezone);

  const today = new Date();
  const currentDate = dayjs(today).format("MM/DD/YY");

  useEffect(() => {
    const updatePhone = async () => {
      const { office_hours, offline_phone_number, online_phone_number } =
        phone_number;
      const startTime = splitHoursMinutes(office_hours.start_time);
      const endTime = splitHoursMinutes(office_hours.end_time);

      const isOpen = await isOfficeHours({
        startTime,
        endTime,
        isHome,
        currentSlug,
      });
      if (isOpen) {
        setConditionalPhone(online_phone_number);
        return;
      }
      setConditionalPhone("");
    };

    if (phone_number) {
      updatePhone();
    }
  }, [phone_number]);

  const nav = siteOptions.footer_navigation;

  return (
    <FooterStyles>
      <div className="container">
        <div className="footer-main">
          <div className="footer-left">
            <Link href="/">
              <a
                className="logo-footer"
                title="Go to 1800-Accountant homepage"
                data-cy="header-site-logo"
              >
                <img
                  className="logo-img"
                  src="/static/images/long-logo.svg"
                  alt="1-800 Accountant Logo"
                  width="225px"
                  height="50px"
                />
              </a>
            </Link>
          </div>
          <div className="footer-middle">
            {nav.map((section) => (
              <div
                key={section.title}
                className={`middle-section ${section.title.toLowerCase()}`}
              >
                <div className="header-title">{section.title}</div>
                {section.links.map((link, index) => (
                  <Link key={index} href={link.url}>
                    <a>
                      <p>{link.text}</p>
                    </a>
                  </Link>
                ))}
                {section.subsection && (
                  <div className="subsection">
                    <div className="header-title">
                      {section.subsection.title}
                    </div>
                    {section.subsection.links.map((link, index) => (
                      <Link key={index} href={link.url}>
                        <a>
                          <p>{link.text}</p>
                        </a>
                      </Link>
                    ))}
                  </div>
                )}
                {section.subsection_2 && (
                  <div className="subsection-2">
                    <div className="header-title">
                      {section.subsection_2.title}
                    </div>
                    {section.subsection_2.links.map((link, index) => (
                      <Link key={index} href={link.url}>
                        <a>
                          <p>{link.text}</p>
                        </a>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="footer-right">
            <div className="footer-icons">
              <div className="connection-container">
                <div className="header-title">CONNECT</div>
                {conditionalPhone ? (
                  holidays.includes(currentDate) && router.pathname != "/" ? (
                    ""
                  ) : (
                    <div className="phone-container">
                      <ReactSVG
                        src="/static/images/icon-awesome-phone-square-alt.svg"
                        alt="awesome-phone-square-alt"
                      />
                      <a href={`tel:${conditionalPhone}`}>
                        {formatPhone(conditionalPhone)}
                      </a>
                    </div>
                  )
                ) : (
                  <div className="hours-container">
                    <div className="title">Office Hours:</div>
                    <div className="days">Monday - Friday</div>
                    <div className="hours">9:00am - 6:00pm EST</div>
                  </div>
                )}
              </div>
              <div className="footer-right-body-container">
                <div className="social-container">
                  {socialLinks.map((socialLink, idx) => {
                    return (
                      <a
                        className={idx !== 0 ? "ml-3 mt-4" : "mt-4"}
                        href={socialLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={`social-icon-${idx}`}
                        onMouseOver={() => setMouseOver(idx)}
                        onMouseLeave={() => setMouseOver(null)}
                      >
                        {/* icon to show hover. Without two svgs icons will have a slight re render time adjusting the entire element. */}
                        <ReactSVG
                          src={`${socialLink.hoverIcon}?type=svg`}
                          className="icon"
                          style={{
                            display: mouseOver === idx ? "block" : "none",
                          }}
                        />
                        <ReactSVG
                          src={`${socialLink.icon}?type=svg`}
                          className="icon"
                          style={{
                            display: mouseOver === idx ? "none" : "block",
                          }}
                        />
                      </a>
                    );
                  })}
                </div>
                <div className="bbb-seal">
                  <a
                    href="https://www.bbb.org/new-york-city/business-reviews/accountant/1800accountant-in-new-york-ny-75159/#sealclick"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://seal-newyork.bbb.org/seals/gray-seal-200-42-whitetxt-bbb-75159.png"
                      alt="BBB Seal"
                      width="200px"
                      height="42px"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="foot-notes">
          <div className="row justify-content-between">
            {isMobile && (
              <div className="term-links-col white-space">
                <p>
                  <a href="/terms-of-services"> Terms of Service </a>|
                  <a href="/privacy-policy"> Privacy Policy </a>
                  {"\n"}
                  <a href="https://protect-us.mimecast.com/s/XrGWCBBj4PFV9pgzTzkC4-?domain=clicktime.symantec.com">
                    {" "}
                    Transparency in Coverage{" "}
                  </a> |
                  <a href="/data-removal"> Do Not Sell My Information </a>
                </p>
              </div>
            )}
            {!isMobile && (
              <div className="term-links-col">
                <p>
                  <a href="/terms-of-services"> Terms of Service </a>|
                  <a href="/privacy-policy"> Privacy Policy </a> |
                  <a href="https://protect-us.mimecast.com/s/XrGWCBBj4PFV9pgzTzkC4-?domain=clicktime.symantec.com">
                    {" "}
                    Transparency in Coverage{" "}
                  </a> |
                  <a href="/data-removal"> Do Not Sell My Information </a>
                </p>
              </div>
            )}
            <div className="col-lg-6">
              <p>
                1-800Accountant Business Services. &copy;{" "}
                {new Date().getFullYear()} All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FooterStyles>
  );
};

export default React.memo(FooterComponent);
